import { retryLazyLoad } from "@root/shared/utils/retryLazyLoad";
import { type FC, useMemo, type SyntheticEvent, Suspense, lazy, type ComponentProps } from "react";
import type { HorizontalBar as HorizontalBarType } from "react-chartjs-2";

const HorizontalBarComponent = lazy(() =>
  retryLazyLoad<ComponentProps<typeof HorizontalBarType>>({
    fn: () => import("react-chartjs-2").then(({ HorizontalBar }) => ({ default: HorizontalBar })),
  }),
);

type Props = {
  ratioData: RatioDataProps;
};

type RatioDataProps = {
  // left
  leftItemRate: number;
  leftItemLabel?: string;
  leftItemBgColor: string;
  leftItemBorderColor?: string;
  // right
  rightItemRate: number;
  rightItemLabel?: string;
  rightItemBgColor: string;
  rightItemBorderColor?: string;
};

export const RatioChart: FC<Props> = ({ ratioData }) => {
  const displayLabels = !!(ratioData.leftItemLabel || ratioData.rightItemLabel);
  const isShowLeftItemBorder = !!ratioData.leftItemBorderColor;
  const isShowRightItemBorder = !!ratioData.rightItemBorderColor;
  const data = useMemo(
    () => ({
      labels: [""],
      datasets: [
        {
          label: ratioData.leftItemLabel,
          data: [ratioData.leftItemRate],
          backgroundColor: ratioData.leftItemBgColor,
          borderColor: [isShowLeftItemBorder ? ratioData.leftItemBorderColor : ""],
          borderWidth: isShowLeftItemBorder ? 1 : 0,
        },
        {
          label: ratioData.rightItemLabel,
          data: [ratioData.rightItemRate],
          backgroundColor: ratioData.rightItemBgColor,
          borderColor: [isShowRightItemBorder ? ratioData.rightItemBorderColor : ""],
          borderWidth: isShowRightItemBorder ? 1 : 0,
        },
      ],
    }),
    [
      isShowLeftItemBorder,
      isShowRightItemBorder,
      ratioData.leftItemBgColor,
      ratioData.leftItemBorderColor,
      ratioData.leftItemLabel,
      ratioData.leftItemRate,
      ratioData.rightItemBgColor,
      ratioData.rightItemBorderColor,
      ratioData.rightItemLabel,
      ratioData.rightItemRate,
    ],
  );

  const options = {
    animation: {
      duration: 0,
    },
    tooltips: {
      enabled: false,
    },
    hover: {
      mode: null,
    },
    scales: {
      xAxes: [
        {
          stacked: true,
          gridLines: {
            display: false,
          },
          ticks: {
            display: false,
            padding: 0,
            beginAtZero: true,
          },
        },
      ],
      yAxes: [
        {
          stacked: true,
          barThickness: 40,
          gridLines: {
            display: false,
            tickMarkLength: -6,
          },
          ticks: {
            padding: 0,
          },
        },
      ],
    },
    legend: {
      onClick: (e: SyntheticEvent) => e.stopPropagation(),
      display: displayLabels,
      position: "bottom",
      labels: {
        boxWidth: 12,
      },
    },
    maintainAspectRatio: false,
  };

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <HorizontalBarComponent options={options} data={data} />
    </Suspense>
  );
};
